import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import api from '../../../../services/api';

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'default' },
    sent: { label: 'Enviada', color: 'warning' },
    received: { label: 'Recebida', color: 'info' },
    won: { label: 'Vencedora', color: 'success' },
    lost: { label: 'Perdedora', color: 'error' }
};

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const ViewPurchaseQuotation = () => {
    const { id } = useParams();
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [quotation, setQuotation] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [working, setWorking] = useState(false);
    const [itemDrafts, setItemDrafts] = useState({});

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId || !id || id === 'new') return;
        carregarCotacao();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, id]);

    const carregarCotacao = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/purchase_quotations/${id}/`);
            setQuotation(res.data);
            const drafts = {};
            (res.data.items || []).forEach((item) => {
                drafts[item.id] = {
                    valor_unitario_cotado: item.valor_unitario_cotado ?? '',
                    prazo_entrega_dias: item.prazo_entrega_dias ?? ''
                };
            });
            setItemDrafts(drafts);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar cotação' });
        }
    };

    const handleSaveItem = async (itemId) => {
        const draft = itemDrafts[itemId];
        try {
            await api.patch(`v1/apps/${appId}/purchase_quotations/${id}/items/${itemId}/`, {
                valor_unitario_cotado: draft.valor_unitario_cotado === '' ? null : draft.valor_unitario_cotado,
                prazo_entrega_dias: draft.prazo_entrega_dias === '' ? null : draft.prazo_entrega_dias
            });
            await carregarCotacao();
            setStatus({ type: '', message: '' });
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao salvar item' });
        }
    };

    const runAction = async (action, successMessage) => {
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/purchase_quotations/${id}/${action}/`, {});
            setQuotation(res.data);
            setStatus({ type: 'success', message: successMessage });
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao executar ação' });
        } finally {
            setWorking(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Excluir esta cotação em rascunho?')) return;
        setWorking(true);
        try {
            await api.delete(`v1/apps/${appId}/purchase_quotations/${id}/`);
            history.push(`/dashboard/purchasing/requests/${quotation.purchase_request}/`);
        } catch (error) {
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao excluir cotação' });
            setWorking(false);
        }
    };

    if (!quotation) {
        return <Box>{status.message && <Alert severity={status.type}>{status.message}</Alert>}</Box>;
    }

    const statusInfo = STATUS_LABEL[quotation.status] || { label: quotation.status, color: 'default' };
    const canEditItems = quotation.status === 'sent' || quotation.status === 'received';

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h3">Cotação - {quotation.organization_name}</Typography>
                <Chip label={statusInfo.label} color={statusInfo.color} />
            </Stack>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Solicitação de origem
                            </Typography>
                            <Typography>
                                <RouterLink to={`/dashboard/purchasing/requests/${quotation.purchase_request}/`}>Ver solicitação</RouterLink>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Validade da proposta
                            </Typography>
                            <Typography>{quotation.valid_until ? new Date(quotation.valid_until).toLocaleDateString('pt-BR') : '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Total cotado
                            </Typography>
                            <Typography>{formatCurrencyBR(quotation.total_cotado)}</Typography>
                        </Grid>
                        {quotation.notes && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Observações
                                </Typography>
                                <Typography>{quotation.notes}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Item</TableCell>
                                <TableCell align="right">Quantidade</TableCell>
                                <TableCell align="right">Preço cotado</TableCell>
                                <TableCell align="right">Prazo (dias)</TableCell>
                                <TableCell align="right">Subtotal</TableCell>
                                <TableCell>Vencedor</TableCell>
                                {canEditItems && <TableCell align="right" />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {quotation.items?.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>{item.produto_variacao_nome || item.service_nome}</TableCell>
                                    <TableCell align="right">{item.quantidade}</TableCell>
                                    <TableCell align="right">
                                        {canEditItems ? (
                                            <TextField
                                                size="small"
                                                type="number"
                                                sx={{ width: 110 }}
                                                value={itemDrafts[item.id]?.valor_unitario_cotado ?? ''}
                                                onChange={(e) =>
                                                    setItemDrafts({
                                                        ...itemDrafts,
                                                        [item.id]: { ...itemDrafts[item.id], valor_unitario_cotado: e.target.value }
                                                    })
                                                }
                                                onBlur={() => handleSaveItem(item.id)}
                                            />
                                        ) : (
                                            formatCurrencyBR(item.valor_unitario_cotado)
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {canEditItems ? (
                                            <TextField
                                                size="small"
                                                type="number"
                                                sx={{ width: 90 }}
                                                value={itemDrafts[item.id]?.prazo_entrega_dias ?? ''}
                                                onChange={(e) =>
                                                    setItemDrafts({
                                                        ...itemDrafts,
                                                        [item.id]: { ...itemDrafts[item.id], prazo_entrega_dias: e.target.value }
                                                    })
                                                }
                                                onBlur={() => handleSaveItem(item.id)}
                                            />
                                        ) : (
                                            item.prazo_entrega_dias ?? '-'
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{formatCurrencyBR(item.subtotal_cotado)}</TableCell>
                                    <TableCell>
                                        {item.is_winner && <Chip label="Vencedor" color="success" size="small" />}
                                        {!item.is_winner && item.ordered_at && <Chip label="Pedido gerado" size="small" variant="outlined" />}
                                    </TableCell>
                                    {canEditItems && (
                                        <TableCell align="right">
                                            {quotation.status === 'received' && !item.is_winner && (
                                                <Button
                                                    size="small"
                                                    onClick={async () => {
                                                        await api.post(
                                                            `v1/apps/${appId}/purchase_quotations/${id}/items/${item.id}/mark-winner/`,
                                                            {}
                                                        );
                                                        carregarCotacao();
                                                    }}
                                                >
                                                    Marcar vencedor
                                                </Button>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={() => history.push(`/dashboard/purchasing/requests/${quotation.purchase_request}/`)}>Voltar</Button>

                {quotation.status === 'draft' && (
                    <>
                        <Button color="error" variant="outlined" disabled={working} onClick={handleDelete}>
                            Excluir
                        </Button>
                        <Button variant="contained" disabled={working} onClick={() => runAction('send', 'Cotação enviada ao fornecedor.')}>
                            Enviar ao Fornecedor
                        </Button>
                    </>
                )}

                {quotation.status === 'sent' && (
                    <>
                        <Button color="error" variant="outlined" disabled={working} onClick={() => runAction('mark-lost', 'Cotação marcada como perdedora.')}>
                            Marcar como Perdedora
                        </Button>
                        <Button
                            variant="contained"
                            disabled={working}
                            onClick={() => runAction('mark-received', 'Cotação marcada como recebida.')}
                        >
                            Marcar como Recebida
                        </Button>
                    </>
                )}

                {quotation.status === 'received' && (
                    <>
                        <Button color="error" variant="outlined" disabled={working} onClick={() => runAction('mark-lost', 'Cotação marcada como perdedora.')}>
                            Marcar como Perdedora
                        </Button>
                        <Button variant="contained" disabled={working} onClick={() => runAction('mark-won', 'Cotação marcada como vencedora.')}>
                            Marcar Cotação Inteira como Vencedora
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default ViewPurchaseQuotation;
